<template>
  <div>
    <b-table
      :items="contacts"
      :fields="fields"
      :busy="loading"
      small
      responsive
      show-empty
      :empty-text="$t('partner.contacts.empty')"
    >
      <template #table-busy>
        <div class="text-center my-3">
          <b-spinner variant="secondary" />
        </div>
      </template>

      <template #cell(name)="{ item }">
        {{ displayName(item) }}
      </template>

      <template #cell(email)="{ item }">
        <a v-if="item.email" :href="`mailto:${item.email}`">{{ item.email }}</a>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(phone)="{ item }">
        <a v-if="item.phone" :href="`tel:${item.phone}`">{{ item.phone }}</a>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(mobilePhone)="{ item }">
        <a v-if="item.mobilePhone" :href="`tel:${item.mobilePhone}`">
          {{ item.mobilePhone }}
        </a>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(actions)="{ item }">
        <div class="d-flex justify-content-end">
          <b-button
            size="sm"
            variant="outline-primary"
            class="mr-1"
            :disabled="!item.phone && !item.mobilePhone"
            @click="openCallModal(item)"
          >
            <i class="material-icons align-middle">phone</i>
            {{ $t('partner.contacts.call') }}
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-1"
            :disabled="!item.email"
            @click="copyEmail(item.email)"
          >
            <i class="material-icons align-middle">content_copy</i>
            {{ $t('partner.contacts.copy-email') }}
          </b-button>
          <b-button
            size="sm"
            variant="outline-success"
            @click="loginAsUser(item)"
          >
            <i class="material-icons align-middle">login</i>
            {{ $t('partner.contacts.login-as') }}
          </b-button>
        </div>
      </template>
    </b-table>

    <CallContactModal
      :visible="showCallModal"
      :contactList="callContactList"
      @hide="showCallModal = false"
      @call-initiated="onCallInitiated"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import { appUrl } from '@gid/vue-common/app-settings/appUrls';
import CallContactModal from '@/components/CallContactModal.vue';

export default {
  name: 'PartnerContacts',
  components: {
    CallContactModal,
  },
  props: {
    partner: {
      type: Object,
      required: true,
    },
    includeChildren: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      contacts: [],
      loading: false,
      showCallModal: false,
      callContactList: [],
    };
  },
  computed: {
    fields() {
      return [
        { key: 'name', label: this.$t('partner.contacts.name') },
        { key: 'email', label: this.$t('partner.contacts.email') },
        { key: 'phone', label: this.$t('partner.contacts.phone') },
        { key: 'mobilePhone', label: this.$t('partner.contacts.mobile') },
        {
          key: 'actions',
          label: this.$t('partner.contacts.actions'),
          class: 'text-right',
        },
      ];
    },
  },
  watch: {
    partner: {
      immediate: true,
      handler() {
        this.fetchContacts();
      },
    },
    includeChildren() {
      this.fetchContacts();
    },
  },
  methods: {
    displayName(contact) {
      const name = [contact.firstName, contact.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      return name || contact.email || '—';
    },
    async fetchContacts() {
      if (!this.partner?.account?.id) {
        this.contacts = [];
        return;
      }
      const accountIds =
        this.includeChildren && this.partner.child_account_ids?.length
          ? [this.partner.account.id, ...this.partner.child_account_ids]
          : [this.partner.account.id];

      this.loading = true;
      try {
        const query = RequestQueryBuilder.create({
          filter: [
            {
              field: 'accountId',
              operator: CondOperator.IN,
              value: accountIds,
            },
          ],
          join: [{ field: 'account', select: ['role', 'name'] }],
          sort: [{ field: 'lastName', order: 'ASC' }],
        }).query();

        const { data } = await axios.get(`/data-api/contact?${query}`);
        this.contacts = (data || []).filter((contact) => !contact.isDeleted);
      } catch (error) {
        console.error('Error loading partner contacts:', error);
        this.$bvToast.toast(this.$t('partner.contacts.error.load'), {
          title: this.$t('partner.contacts.error.title'),
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    openCallModal(contact) {
      this.callContactList = [contact];
      this.showCallModal = true;
    },
    onCallInitiated(selectedContact) {
      if (selectedContact?.phone) {
        window.open(`tel:${selectedContact.phone}`);
      }
      this.showCallModal = false;
    },
    async copyEmail(email) {
      if (!email) {
        return;
      }
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(email);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = email;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        this.$bvToast.toast(
          this.$t('partner.contacts.email-copied', { email }),
          {
            title: this.$t('partner.contacts.success-title'),
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right',
          },
        );
      } catch (error) {
        console.error('Failed to copy email:', error);
      }
    },
    async loginAsUser(contact) {
      const userRole = contact?.account?.role;
      try {
        const {
          data: { refresh_token },
        } = await axios.post('/api/admin/refresh-token-for', {
          email: contact.email,
          role: userRole,
          contact_sfid: contact.id,
        });

        const url = appUrl(userRole, `/login?token=${refresh_token}`);
        window.open(url, '_blank');
      } catch (error) {
        console.error(error);
        this.$bvToast.toast(
          this.$t('partner.contacts.error.login', {
            name: this.displayName(contact),
          }),
          {
            title: this.$t('partner.contacts.error.title'),
            variant: 'danger',
            solid: true,
            noAutoHide: true,
          },
        );
      }
    },
  },
};
</script>
